import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, ChevronRight, ArrowRight, User } from "lucide-react";
import { fetchPost, fetchBlog } from "@/lib/api";
import BlogCard from "@/components/rotta/BlogCard";
import BlogGallery from "@/components/rotta/BlogGallery";
import { Reveal } from "@/lib/motion";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [more, setMore] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchPost(slug).then((p) => { setPost(p); setLoading(false); }).catch(() => setLoading(false));
    fetchBlog().then((all) => setMore(all.filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (loading) return <div className="pt-40 text-center text-[#B8B8B8]">Carregando…</div>;
  if (!post) return <div className="pt-40 text-center text-[#B8B8B8]">Artigo não encontrado.</div>;

  const d = new Date(post.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div data-testid={`blog-detail-${post.slug}`}>
      <section className="relative h-[62vh] min-h-[460px] w-full overflow-hidden flex items-end">
        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover slow-zoom" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/40 via-[#0B0B0B]/50 to-[#0B0B0B]" />
        <div className="container-rotta relative z-10 pb-16">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-4 flex items-center gap-2">
            <Link to="/blog" className="hover:text-[#C6A762]">Blog</Link>
            <ChevronRight size={12} /><span className="text-[#C6A762]">{post.category}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.0] max-w-4xl">{post.title}</h1>
          <div className="flex items-center gap-5 text-[11px] tracking-[0.2em] uppercase text-white/70 mt-6">
            <span className="flex items-center gap-2"><User size={12} className="text-[#C6A762]" /> {post.author}</span>
            <span>{d}</span>
            <span className="flex items-center gap-1"><Clock size={12} /> {post.read_time}</span>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rotta">
          {/* Galeria: carrossel logo abaixo do título/subtítulo, antes do texto */}
          {post.gallery?.length > 0 && (
            <div className="mb-12" data-testid="blog-gallery-wrap">
              <BlogGallery images={post.gallery} onOpen={setLightbox} />
            </div>
          )}

          <div className="w-full max-w-3xl mx-auto">
            <Reveal>
              {post.excerpt && <p className="text-xl text-white/90 leading-relaxed mb-8 font-body">{post.excerpt}</p>}
              <div
                data-testid="blog-content"
                className="blog-content text-[#D6D6D6] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content || "" }}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {more.length > 0 && (
        <section className="section pt-0">
          <div className="container-rotta">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="flex items-center gap-3 text-[#C6A762] mb-4"><span className="gold-divider" /><span className="text-[11px] tracking-[0.35em] uppercase">Continue lendo</span></div>
                <h2 className="font-display text-4xl text-white">Mais artigos.</h2>
              </div>
              <Link to="/blog" className="text-[#C6A762] text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 hover:translate-x-1 transition-transform">
                Ver blog <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid gap-7 md:grid-cols-3">
              {more.map((p) => <BlogCard key={p.slug} post={p} />)}
            </div>
          </div>
        </section>
      )}

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          data-testid="blog-lightbox"
          className="fixed inset-0 z-[100] bg-[#0B0B0B]/95 backdrop-blur grid place-items-center p-6 cursor-zoom-out"
        >
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}
